import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useDirection } from '../context/DirectionContext';

export interface Column<T> { key: string; header: string; render(row: T): ReactNode }

export function DataTable<T>({ rows, columns, empty = 'No records' }: { rows: T[]; columns: Column<T>[]; empty?: string }) {
  const { t } = useDirection();
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(row => {
      try { return JSON.stringify(row).toLowerCase().includes(term); }
      catch { return true; }
    });
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  useEffect(() => { setPage(1); }, [search, pageSize]);

  if (!rows.length) return <div className="empty">{empty}</div>;

  const start = (page - 1) * pageSize;
  const visible = filtered.slice(start, start + pageSize);
  const from = filtered.length ? start + 1 : 0;
  const to = Math.min(start + pageSize, filtered.length);

  return <div className="datatable">
    <div className="datatable-toolbar">
      <label className="datatable-length">{t('showEntries')} <select value={pageSize} onChange={event => setPageSize(Number(event.target.value))}>{[10, 25, 50, 100].map(value => <option key={value} value={value}>{value}</option>)}</select> {t('entries')}</label>
      <label className="datatable-search">{t('tableSearch')} <input type="search" value={search} onChange={event => setSearch(event.target.value)} /></label>
    </div>
    <div className="table-wrap">
      <table>
        <thead><tr>{columns.map(column => <th key={column.key}>{column.header}</th>)}</tr></thead>
        <tbody>{visible.length ? visible.map((row, index) => <tr key={(row as any).id ?? start + index}>{columns.map(column => <td key={column.key}>{column.render(row)}</td>)}</tr>) : <tr><td colSpan={columns.length} className="empty-cell">{empty}</td></tr>}</tbody>
      </table>
    </div>
    <div className="datatable-footer">
      <span>{t('showing')} {from}–{to} / {filtered.length}</span>
      <div className="pagination">
        <button className="secondary" disabled={page <= 1} onClick={() => setPage(value => Math.max(1, value - 1))}>{t('previous')}</button>
        <span>{page} / {totalPages}</span>
        <button className="secondary" disabled={page >= totalPages} onClick={() => setPage(value => Math.min(totalPages, value + 1))}>{t('next')}</button>
      </div>
    </div>
  </div>;
}
